export const helpArticles = {
  // Getting Started Articles
  "how-to-create-your-first-invoice": {
    id: "how-to-create-your-first-invoice",
    title: "How to Create Your First Invoice",
    category: "Getting Started",
    description: "Learn how to create and submit your first invoice using FBRSTAPP",
    readTime: "5 min read",
    content: [
      {
        type: "paragraph",
        text: "Creating your first invoice with FBRSTAPP is simple and straightforward. This guide will walk you through the entire process from start to finish."
      },
      {
        type: "heading",
        text: "Prerequisites"
      },
      {
        type: "list",
        items: [
          "Active FBRSTAPP account",
          "Valid FBR credentials configured",
          "Business information completed in your profile",
          "Internet connection for FBR API submission"
        ]
      },
      {
        type: "heading",
        text: "Step-by-Step Process"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Navigate to Invoice Creation",
            description: "From your dashboard, click on 'Create New Invoice' or go to Invoice → New Invoice from the menu."
          },
          {
            title: "Select Invoice Type",
            description: "Choose the appropriate invoice type based on your transaction (Standard Rate, Zero Rate, Exempt, etc.)."
          },
          {
            title: "Fill Buyer Information",
            description: "Enter the buyer's details including name, NTN (if registered), address, and contact information."
          },
          {
            title: "Add Invoice Items",
            description: "Add products or services with quantities, rates, and applicable taxes. The system will automatically calculate totals."
          },
          {
            title: "Review and Validate",
            description: "Review all information for accuracy. The system will highlight any validation errors."
          },
          {
            title: "Submit to FBR",
            description: "Click 'Submit to FBR' to send the invoice to Pakistan's Federal Board of Revenue for processing."
          }
        ]
      },
      {
        type: "info",
        text: "The system automatically generates QR codes and invoice numbers as per FBR requirements."
      },
      {
        type: "heading",
        text: "Common Issues and Solutions"
      },
      {
        type: "paragraph",
        text: "If you encounter any issues during invoice creation:"
      },
      {
        type: "list",
        items: [
          "Validation Errors: Check all required fields are filled correctly",
          "Tax Calculation Issues: Verify tax rates match FBR requirements",
          "Buyer Information: Ensure NTN format is correct for registered buyers",
          "API Connection: Check your internet connection and FBR API status"
        ]
      },
      {
        type: "warning",
        text: "Always verify buyer information and tax calculations before submitting to FBR. Incorrect submissions may result in penalties."
      }
    ],
    relatedArticles: [
      {
        title: "Setting up your FBR credentials",
        description: "Learn how to configure your FBR API credentials"
      },
      {
        title: "Understanding tax calculations",
        description: "Guide to tax rates and calculations in Pakistan"
      }
    ]
  },

  "setting-up-your-fbr-credentials": {
    id: "setting-up-your-fbr-credentials",
    title: "Setting Up Your FBR Credentials",
    category: "Getting Started",
    description: "Configure your FBR API credentials to connect with Pakistan's tax system",
    readTime: "7 min read",
    content: [
      {
        type: "paragraph",
        text: "To use FBRSTAPP effectively, you need to configure your FBR (Federal Board of Revenue) credentials. This allows the system to submit invoices directly to Pakistan's tax authority."
      },
      {
        type: "heading",
        text: "What You'll Need"
      },
      {
        type: "list",
        items: [
          "Valid NTN (National Tax Number)",
          "FBR Digital Invoicing credentials",
          "Business registration documents",
          "Authorized person's CNIC"
        ]
      },
      {
        type: "heading",
        text: "Getting FBR Credentials"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Visit FBR Portal",
            description: "Go to the official FBR website (fbr.gov.pk) and navigate to Digital Invoicing section."
          },
          {
            title: "Register for Digital Invoicing",
            description: "Complete the registration process with your business details and NTN."
          },
          {
            title: "Obtain API Credentials",
            description: "After approval, you'll receive API credentials including client ID and secret."
          },
          {
            title: "Download Certificates",
            description: "Download the required SSL certificates for secure communication."
          }
        ]
      },
      {
        type: "heading",
        text: "Configuring in FBRSTAPP"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Access Settings",
            description: "Go to Profile → FBR Settings in your FBRSTAPP dashboard."
          },
          {
            title: "Enter Credentials",
            description: "Input your FBR API credentials, NTN, and business information."
          },
          {
            title: "Upload Certificates",
            description: "Upload the SSL certificates downloaded from FBR portal."
          },
          {
            title: "Test Connection",
            description: "Use the 'Test Connection' button to verify your setup."
          }
        ]
      },
      {
        type: "info",
        text: "Keep your FBR credentials secure and never share them with unauthorized persons."
      },
      {
        type: "warning",
        text: "Incorrect credentials will prevent invoice submission to FBR. Always verify your setup before going live."
      }
    ],
    relatedArticles: [
      {
        title: "Understanding the dashboard",
        description: "Navigate your FBRSTAPP dashboard effectively"
      },
      {
        title: "Troubleshooting API connections",
        description: "Fix common FBR API connection issues"
      }
    ]
  },

  "understanding-the-dashboard": {
    id: "understanding-the-dashboard",
    title: "Understanding the Dashboard",
    category: "Getting Started",
    description: "Navigate and utilize your FBRSTAPP dashboard effectively",
    readTime: "4 min read",
    content: [
      {
        type: "paragraph",
        text: "Your FBRSTAPP dashboard is the central hub for managing all your invoicing activities. This guide explains each section and how to use them effectively."
      },
      {
        type: "heading",
        text: "Dashboard Overview"
      },
      {
        type: "paragraph",
        text: "The dashboard is divided into several key sections:"
      },
      {
        type: "list",
        items: [
          "Quick Stats: Overview of your invoicing activity",
          "Recent Invoices: Latest invoice submissions and their status",
          "FBR Status: Real-time connection status with FBR API",
          "Quick Actions: Fast access to common tasks",
          "Notifications: Important alerts and updates"
        ]
      },
      {
        type: "heading",
        text: "Navigation Menu"
      },
      {
        type: "paragraph",
        text: "The main navigation includes:"
      },
      {
        type: "list",
        items: [
          "Dashboard: Main overview page",
          "Invoices: Create, view, and manage invoices",
          "Reports: Generate various business reports",
          "Settings: Configure your account and FBR settings",
          "Support: Access help and contact support"
        ]
      },
      {
        type: "heading",
        text: "Quick Stats Explained"
      },
      {
        type: "list",
        items: [
          "Total Invoices: Number of invoices created this month",
          "Submitted: Successfully submitted to FBR",
          "Pending: Awaiting submission or approval",
          "Rejected: Invoices rejected by FBR (need attention)"
        ]
      },
      {
        type: "info",
        text: "The dashboard updates in real-time, so you always have the latest information about your invoicing status."
      }
    ],
    relatedArticles: [
      {
        title: "How to create your first invoice",
        description: "Step-by-step invoice creation guide"
      },
      {
        title: "Managing invoice status",
        description: "Track and manage your invoice submissions"
      }
    ]
  },

  "connecting-to-fbr-api": {
    id: "connecting-to-fbr-api",
    title: "Connecting to FBR API",
    category: "Getting Started",
    description: "Establish and maintain connection with Pakistan's FBR API system",
    readTime: "6 min read",
    content: [
      {
        type: "paragraph",
        text: "The FBR API connection is crucial for submitting invoices to Pakistan's tax system. This guide covers connection setup, troubleshooting, and best practices."
      },
      {
        type: "heading",
        text: "Connection Requirements"
      },
      {
        type: "list",
        items: [
          "Valid FBR credentials (Client ID and Secret)",
          "SSL certificates from FBR",
          "Stable internet connection",
          "Whitelisted IP address (if required)"
        ]
      },
      {
        type: "heading",
        text: "Testing Your Connection"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Access API Settings",
            description: "Go to Settings → FBR API Configuration in your dashboard."
          },
          {
            title: "Verify Credentials",
            description: "Ensure all credentials are entered correctly and certificates are uploaded."
          },
          {
            title: "Run Connection Test",
            description: "Click 'Test Connection' to verify communication with FBR servers."
          },
          {
            title: "Check Status Indicators",
            description: "Green indicators show successful connection, red indicates issues."
          }
        ]
      },
      {
        type: "heading",
        text: "Common Connection Issues"
      },
      {
        type: "list",
        items: [
          "Invalid Credentials: Double-check your FBR API credentials",
          "Certificate Errors: Ensure SSL certificates are valid and properly uploaded",
          "Network Issues: Check internet connection and firewall settings",
          "IP Whitelisting: Verify your IP is whitelisted with FBR if required"
        ]
      },
      {
        type: "warning",
        text: "FBR API has rate limits. Avoid making too many requests in a short time to prevent temporary blocks."
      },
      {
        type: "info",
        text: "FBRSTAPP automatically handles API authentication and token refresh for seamless operation."
      }
    ],
    relatedArticles: [
      {
        title: "Setting up your FBR credentials",
        description: "Configure your FBR API credentials"
      },
      {
        title: "Troubleshooting submission errors",
        description: "Fix common invoice submission issues"
      }
    ]
  },

  // Invoice Management Articles
  "creating-and-editing-invoices": {
    id: "creating-and-editing-invoices",
    title: "Creating and Editing Invoices",
    category: "Invoice Management",
    description: "Comprehensive guide to invoice creation and modification",
    readTime: "8 min read",
    content: [
      {
        type: "paragraph",
        text: "FBRSTAPP provides powerful tools for creating and editing invoices that comply with Pakistan's FBR requirements. This guide covers all aspects of invoice management."
      },
      {
        type: "heading",
        text: "Invoice Types Available"
      },
      {
        type: "list",
        items: [
          "Standard Rate Invoices (17% tax)",
          "Zero Rate Invoices (0% tax for exports)",
          "Exempt Invoices (tax-exempt goods/services)",
          "Reduced Rate Invoices (special tax rates)",
          "Service Invoices (for service providers)"
        ]
      },
      {
        type: "heading",
        text: "Creating a New Invoice"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Choose Invoice Type",
            description: "Select the appropriate invoice type based on your transaction."
          },
          {
            title: "Enter Buyer Details",
            description: "Fill in buyer information including name, NTN, address, and contact details."
          },
          {
            title: "Add Line Items",
            description: "Add products or services with descriptions, quantities, rates, and tax information."
          },
          {
            title: "Apply Discounts",
            description: "Add any applicable discounts at line item or invoice level."
          },
          {
            title: "Review Calculations",
            description: "Verify all tax calculations and totals are correct."
          },
          {
            title: "Save or Submit",
            description: "Save as draft or submit directly to FBR."
          }
        ]
      },
      {
        type: "heading",
        text: "Editing Existing Invoices"
      },
      {
        type: "paragraph",
        text: "You can edit invoices that haven't been submitted to FBR:"
      },
      {
        type: "list",
        items: [
          "Draft invoices can be fully edited",
          "Submitted invoices cannot be modified",
          "Rejected invoices can be corrected and resubmitted",
          "Use 'Duplicate' feature to create similar invoices"
        ]
      },
      {
        type: "warning",
        text: "Once an invoice is successfully submitted to FBR, it cannot be edited. Create a credit note for corrections."
      },
      {
        type: "info",
        text: "The system automatically calculates taxes based on current FBR rates and validates all required fields."
      }
    ],
    relatedArticles: [
      {
        title: "Understanding tax calculations",
        description: "Learn about tax rates and calculations"
      },
      {
        title: "Handling invoice rejections",
        description: "Fix and resubmit rejected invoices"
      }
    ]
  },

  "submitting-to-fbr": {
    id: "submitting-to-fbr",
    title: "Submitting Invoices to FBR",
    category: "Invoice Management",
    description: "Learn how to successfully submit invoices to Pakistan's FBR system",
    readTime: "6 min read",
    content: [
      {
        type: "paragraph",
        text: "Submitting invoices to FBR is a critical step in Pakistan's digital invoicing system. This guide ensures your submissions are successful and compliant."
      },
      {
        type: "heading",
        text: "Pre-Submission Checklist"
      },
      {
        type: "list",
        items: [
          "All required fields are completed",
          "Buyer information is accurate",
          "Tax calculations are correct",
          "FBR API connection is active",
          "Invoice format complies with FBR standards"
        ]
      },
      {
        type: "heading",
        text: "Submission Process"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Final Review",
            description: "Review all invoice details one last time before submission."
          },
          {
            title: "Click Submit",
            description: "Click the 'Submit to FBR' button on your invoice."
          },
          {
            title: "Wait for Processing",
            description: "The system will process your submission and communicate with FBR."
          },
          {
            title: "Check Status",
            description: "Monitor the submission status in real-time."
          },
          {
            title: "Download Receipt",
            description: "Once accepted, download the FBR receipt and QR code."
          }
        ]
      },
      {
        type: "heading",
        text: "Submission Status Types"
      },
      {
        type: "list",
        items: [
          "Pending: Submission in progress",
          "Accepted: Successfully submitted to FBR",
          "Rejected: FBR rejected the invoice (needs correction)",
          "Error: Technical error during submission"
        ]
      },
      {
        type: "info",
        text: "FBR typically processes submissions within a few seconds to minutes, depending on system load."
      },
      {
        type: "warning",
        text: "Do not submit duplicate invoices. Each invoice should have a unique invoice number."
      }
    ],
    relatedArticles: [
      {
        title: "Handling invoice rejections",
        description: "Fix and resubmit rejected invoices"
      },
      {
        title: "Understanding FBR response codes",
        description: "Interpret FBR system responses"
      }
    ]
  },

  "bulk-operations": {
    id: "bulk-operations",
    title: "Bulk Invoice Operations",
    category: "Invoice Management",
    description: "Learn how to handle multiple invoices efficiently with bulk operations",
    readTime: "4 min read",
    content: [
      {
        type: "paragraph",
        text: "FBRSTAPP provides powerful bulk operations to help you manage multiple invoices efficiently, saving time and reducing manual work."
      },
      {
        type: "heading",
        text: "Available Bulk Operations"
      },
      {
        type: "list",
        items: [
          "Bulk invoice creation from CSV/Excel files",
          "Bulk submission to FBR",
          "Bulk status checking",
          "Bulk export and reporting",
          "Bulk deletion of draft invoices"
        ]
      },
      {
        type: "heading",
        text: "Bulk Invoice Creation"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Prepare Your Data",
            description: "Download the CSV template and fill in your invoice data following the required format."
          },
          {
            title: "Upload File",
            description: "Go to Invoices → Bulk Import and upload your CSV file."
          },
          {
            title: "Review and Validate",
            description: "The system will validate all data and show any errors that need correction."
          },
          {
            title: "Create Invoices",
            description: "Once validated, click 'Create Invoices' to generate all invoices in bulk."
          }
        ]
      },
      {
        type: "info",
        text: "You can create up to 1000 invoices at once using bulk operations."
      }
    ]
  },

  "updating-profile": {
    id: "updating-profile",
    title: "Updating Your Profile",
    category: "Account Management",
    description: "Keep your business information up to date",
    readTime: "3 min read",
    content: [
      {
        type: "paragraph",
        text: "Keeping your profile information current is essential for proper invoice generation and FBR compliance."
      },
      {
        type: "heading",
        text: "Profile Information"
      },
      {
        type: "list",
        items: [
          "Business name and registration details",
          "NTN and tax information",
          "Contact information",
          "Business address",
          "Bank account details"
        ]
      },
      {
        type: "warning",
        text: "Changes to NTN or business registration may require re-verification with FBR."
      }
    ]
  },

  "managing-team": {
    id: "managing-team",
    title: "Managing Team Members",
    category: "Account Management", 
    description: "Add and manage team members with different access levels",
    readTime: "5 min read",
    content: [
      {
        type: "paragraph",
        text: "FBRSTAPP allows you to add team members with different roles and permissions to help manage your invoicing workflow."
      },
      {
        type: "heading",
        text: "User Roles"
      },
      {
        type: "list",
        items: [
          "Admin: Full access to all features",
          "Manager: Can create and submit invoices",
          "Operator: Can create invoices but cannot submit",
          "Viewer: Read-only access to invoices and reports"
        ]
      }
    ]
  },

  "billing-subscriptions": {
    id: "billing-subscriptions",
    title: "Billing and Subscriptions",
    category: "Account Management",
    description: "Manage your FBRSTAPP subscription and billing",
    readTime: "4 min read",
    content: [
      {
        type: "paragraph",
        text: "Understand your FBRSTAPP subscription plans and manage your billing information."
      },
      {
        type: "heading",
        text: "Subscription Plans"
      },
      {
        type: "list",
        items: [
          "Basic Plan: Up to 100 invoices per month",
          "Professional Plan: Up to 1000 invoices per month",
          "Enterprise Plan: Unlimited invoices with priority support"
        ]
      }
    ]
  },

  "security-settings": {
    id: "security-settings",
    title: "Security Settings",
    category: "Account Management",
    description: "Configure security settings to protect your account",
    readTime: "6 min read",
    content: [
      {
        type: "paragraph",
        text: "Protect your FBRSTAPP account with proper security settings and best practices."
      },
      {
        type: "heading",
        text: "Security Features"
      },
      {
        type: "list",
        items: [
          "Two-factor authentication (2FA)",
          "Strong password requirements",
          "Session management",
          "Login activity monitoring",
          "IP whitelisting for admin access"
        ]
      },
      {
        type: "warning",
        text: "Always enable 2FA for enhanced account security, especially for admin accounts."
      }
    ]
  },

  "fbr-compliance": {
    id: "fbr-compliance",
    title: "FBR Compliance Requirements",
    category: "Compliance & Security",
    description: "Understand Pakistan's FBR compliance requirements for digital invoicing",
    readTime: "8 min read",
    content: [
      {
        type: "paragraph",
        text: "Pakistan's Federal Board of Revenue has specific requirements for digital invoicing that all businesses must follow."
      },
      {
        type: "heading",
        text: "Key Compliance Requirements"
      },
      {
        type: "list",
        items: [
          "All invoices must be submitted to FBR within 24 hours",
          "QR codes must be generated and printed on invoices",
          "Invoice numbering must follow FBR sequential format",
          "Tax calculations must be accurate and compliant",
          "Buyer information must be complete and verified"
        ]
      },
      {
        type: "heading",
        text: "Penalties for Non-Compliance"
      },
      {
        type: "list",
        items: [
          "Late submission: PKR 1,000 per invoice",
          "Incorrect information: PKR 5,000 per invoice",
          "Missing QR codes: PKR 2,000 per invoice",
          "Repeated violations may result in system blocking"
        ]
      },
      {
        type: "warning",
        text: "Non-compliance with FBR requirements can result in significant penalties and business disruption."
      }
    ]
  },

  "data-security": {
    id: "data-security",
    title: "Data Security Measures",
    category: "Compliance & Security",
    description: "Learn about FBRSTAPP's data security and privacy protection",
    readTime: "5 min read",
    content: [
      {
        type: "paragraph",
        text: "FBRSTAPP implements comprehensive security measures to protect your business data and ensure privacy."
      },
      {
        type: "heading",
        text: "Security Measures"
      },
      {
        type: "list",
        items: [
          "256-bit SSL encryption for all data transmission",
          "AES-256 encryption for data at rest",
          "Regular security audits and penetration testing",
          "SOC 2 Type II compliance",
          "GDPR compliance for data protection"
        ]
      },
      {
        type: "info",
        text: "Your data is stored in secure, geographically distributed data centers with 99.9% uptime guarantee."
      }
    ]
  },

  "audit-trails": {
    id: "audit-trails",
    title: "Audit Trails",
    category: "Compliance & Security",
    description: "Track all system activities with comprehensive audit trails",
    readTime: "4 min read",
    content: [
      {
        type: "paragraph",
        text: "FBRSTAPP maintains detailed audit trails of all system activities for compliance and security purposes."
      },
      {
        type: "heading",
        text: "Tracked Activities"
      },
      {
        type: "list",
        items: [
          "User login and logout activities",
          "Invoice creation, modification, and deletion",
          "FBR submission attempts and responses",
          "Settings and configuration changes",
          "Data export and report generation"
        ]
      }
    ]
  },

  "backup-recovery": {
    id: "backup-recovery",
    title: "Backup and Recovery",
    category: "Compliance & Security",
    description: "Understand data backup and disaster recovery procedures",
    readTime: "3 min read",
    content: [
      {
        type: "paragraph",
        text: "FBRSTAPP implements robust backup and disaster recovery procedures to ensure your data is always safe and accessible."
      },
      {
        type: "heading",
        text: "Backup Schedule"
      },
      {
        type: "list",
        items: [
          "Real-time replication to multiple data centers",
          "Daily automated backups",
          "Weekly full system backups",
          "Monthly long-term archive backups",
          "Point-in-time recovery capabilities"
        ]
      },
      {
        type: "info",
        text: "In case of any issues, we can restore your data to any point within the last 30 days."
      }
    ]
  },

  "handling-rejections": {
    id: "handling-rejections",
    title: "Handling Invoice Rejections",
    category: "Invoice Management",
    description: "Fix and resubmit rejected invoices effectively",
    readTime: "5 min read",
    content: [
      {
        type: "paragraph",
        text: "Sometimes FBR may reject invoice submissions due to various reasons. This guide helps you understand and resolve rejection issues quickly."
      },
      {
        type: "heading",
        text: "Common Rejection Reasons"
      },
      {
        type: "list",
        items: [
          "Invalid buyer NTN format",
          "Incorrect tax calculations",
          "Missing required fields",
          "Duplicate invoice numbers",
          "Invalid item codes or descriptions"
        ]
      },
      {
        type: "heading",
        text: "How to Handle Rejections"
      },
      {
        type: "steps",
        steps: [
          {
            title: "Check Rejection Reason",
            description: "Review the detailed rejection message from FBR."
          },
          {
            title: "Identify the Issue",
            description: "Locate the specific field or data causing the rejection."
          },
          {
            title: "Make Corrections",
            description: "Fix the identified issues in your invoice."
          },
          {
            title: "Resubmit",
            description: "Submit the corrected invoice to FBR again."
          }
        ]
      },
      {
        type: "info",
        text: "FBRSTAPP highlights rejected invoices in red and provides detailed error messages to help you fix issues quickly."
      }
    ]
  }
};

// Helper function to get article by ID
export const getArticleById = (id) => {
  return helpArticles[id] || null;
};

// Helper function to get articles by category
export const getArticlesByCategory = (category) => {
  return Object.values(helpArticles).filter(article => article.category === category);
};

// Helper function to search articles
export const searchArticles = (query) => {
  const lowercaseQuery = query.toLowerCase();
  return Object.values(helpArticles).filter(article => 
    article.title.toLowerCase().includes(lowercaseQuery) ||
    article.description.toLowerCase().includes(lowercaseQuery) ||
    article.category.toLowerCase().includes(lowercaseQuery)
  );
};